// "Investment Ratio" - second section of "IC Reporting", ported from
// reporting-hub/frontend/src/components/preflight/report/InvestmentRatioCard.tsx.
// Per platform, Valuation (capital actually deployed into loans) against
// total Cash (Platform + SPV) still sitting idle - Investment Ratio =
// Valuation / (Valuation + Cash). Unlike Deployment, this reads
// calculation_result.json (general_overview_<PLATFORM>/cash_positions) - 404s
// until "Confirm & Calculate" has run for this reporting, same as Cashflow
// Projection.

import React, { useEffect, useState } from 'react';
import axiosWithAuth from '../../../helpers/AxiosHelper';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Skeleton } from 'primereact/skeleton';
import { formatCurrency, formatPercent, titleCasePlatform } from '../calculation/calcFormat';
import errorMessage from '../../../helpers/errorMessage';

const CASH_STATUS_LABEL = {
  ok: '',
  missing_upload: 'Cash not uploaded',
  not_implemented: 'Not implemented',
  error: 'Cash error',
};

const InvestmentRatioCard = ({ reportingId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!reportingId) return;
    let cancelled = false;
    setLoading(true);
    axiosWithAuth
      .get(`/compartment-report/reportings/${reportingId}/investment-ratio`)
      .then((res) => {
        if (!cancelled) {
          setData(res.data);
          setError(null);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          // 404 until "Confirm & Calculate" has run - not a real error.
          if (err?.response?.status !== 404) {
            console.error('[InvestmentRatioCard] fetch error:', errorMessage(err));
            setError(errorMessage(err, 'Failed to load Investment Ratio.'));
          }
          setData(null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [reportingId]);

  const rows = data && data.available && data.fund ? [{ label: 'Fund', _bold: true, ...data.fund }, ...data.platforms.map((p) => ({ label: titleCasePlatform(p.platform), ...p }))] : [];

  const labelBody = (row) => (
    <span>
      {row.label}
      {row.cash_status && row.cash_status !== 'ok' && <span className="text-color-secondary text-sm ml-2">{CASH_STATUS_LABEL[row.cash_status]}</span>}
    </span>
  );

  return (
    <div className="cr-card cr-report-card">
      <h4 className="cr-card-title cr-card-title-block">Investment Ratio</h4>
      <p className="cr-card-subtitle">
        Valuation vs. total cash (Originator + SPV) per Originator - how much of available capital is actually deployed into loans.
      </p>

      {error ? (
        <div className="ex-inline-error">{error}</div>
      ) : loading ? (
        <Skeleton height="220px" borderRadius="12px" />
      ) : !data || !data.available || !data.fund ? (
        <p className="text-color-secondary">Not available yet - run "Confirm &amp; Calculate" with cash-position uploads to see this.</p>
      ) : (
        <DataTable value={rows} size="small" showGridlines rowClassName={(row) => ({ 'cr-subtotal-row': row._bold })}>
          <Column header="Originator" body={labelBody} />
          <Column header="Valuation" align="right" headerClassName="cr-num" bodyClassName="cr-num" body={(row) => (row.valuation !== null ? formatCurrency(row.valuation) : '–')} />
          <Column header="Cash (Originator + SPV)" align="right" headerClassName="cr-num" bodyClassName="cr-num" body={(row) => (row.cash !== null ? formatCurrency(row.cash) : '–')} />
          <Column header="Total" align="right" headerClassName="cr-num" bodyClassName="cr-num" body={(row) => (row.total !== null ? formatCurrency(row.total) : '–')} />
          <Column header="Investment Ratio" align="right" headerClassName="cr-num" bodyClassName="cr-num" body={(row) => (row.investment_ratio !== null ? formatPercent(row.investment_ratio) : '–')} />
        </DataTable>
      )}
    </div>
  );
};

export default InvestmentRatioCard;
